import React, { useState } from "react";

import { faCircleCheck, faCircleXmark, faGlobe, faPencil } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ButtonIcon, Chip, SingleSelectInput, Text, Tooltip } from "@performyard/py-components";

interface CompetencyLite {
	_id: string;
	name: string;
	is_company_wide: boolean;
	levels: Array<{ rank: number; name: string }>;
}

export interface AddCompetencyRowProps {
	index: number;
	competency: CompetencyLite | undefined;
	expectedLevel: number | null;
	onChangeLevel: (level: number | null) => void;
	onRemove: () => void;
}

const AddCompetencyRow: React.FC<AddCompetencyRowProps> = ({
	index,
	competency,
	expectedLevel,
	onChangeLevel,
	onRemove,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [draftLevel, setDraftLevel] = useState<number | null>(expectedLevel);

	// Stale-reference fallback. Reachable when a competency the form references was archived or
	// deleted between page load and now. Always allow removal.
	if (!competency) {
		return (
			<div
				data-testid={`competency-row-${index}`}
				className="flex h-10 items-center justify-between gap-3 rounded-md border border-solid border-neutral-3 bg-neutral-1 pr-3"
			>
				<Text size="paragraph-3" weight="regular" color="neutral-7">
					Competency unavailable — remove to continue
				</Text>
				<ButtonIcon
					variant="standard"
					color="neutral"
					shade={7}
					size={16}
					icon={faCircleXmark}
					aria-label={`Remove competency ${index + 1}`}
					onClick={onRemove}
				/>
			</div>
		);
	}

	const levelOptions = competency.levels.map((lvl) => ({ value: lvl.rank, label: lvl.name }));
	const hasLevels = levelOptions.length > 0;
	const currentLevel = competency.levels.find((l) => l.rank === expectedLevel);

	const enterEdit = () => {
		setDraftLevel(expectedLevel);
		setIsEditing(true);
	};
	const confirmEdit = () => {
		onChangeLevel(draftLevel);
		setIsEditing(false);
	};
	const cancelEdit = () => {
		setDraftLevel(expectedLevel);
		setIsEditing(false);
	};

	return (
		<div
			data-testid={`competency-row-${index}`}
			className="flex h-10 items-center gap-3 rounded-md border border-solid border-neutral-3 bg-neutral-1"
		>
			<div className="flex min-w-0 flex-1 items-center gap-1.5 pl-3">
				<Text size="paragraph-2" weight="regular" color="neutral-9">
					<span className="py-line-clamp-1">{competency.name}</span>
				</Text>
				{competency.is_company_wide && (
					<Tooltip content="Company Wide Competency">
						<span
							data-testid={`company-wide-icon-${index}`}
							className="flex size-4 shrink-0 items-center justify-center"
						>
							<FontAwesomeIcon icon={faGlobe} className="text-sm text-primary-5" />
						</span>
					</Tooltip>
				)}
			</div>

			{isEditing ? (
				<>
					<div className="w-48 shrink-0">
						{hasLevels ? (
							<SingleSelectInput<number>
								value={draftLevel ?? undefined}
								placeholder="Make a selection"
								options={levelOptions}
								onChange={(v) => setDraftLevel(v ?? null)}
								isClearable
							/>
						) : (
							<SingleSelectInput<number>
								value={undefined}
								options={[]}
								onChange={() => undefined}
								placeholder="No levels created"
								disabled
							/>
						)}
					</div>
					{/* 88px trailing column matches the picker's Add column and the non-edit row
					    trailing. Two 16px slots (pencil/X) with gap-1, right-anchored so icon
					    positions stay stable across edit/non-edit and CW/non-CW variants. */}
					<div className="box-border grid w-[76px] shrink-0 grid-cols-[1fr_16px_16px] items-center gap-2 pr-3">
						<div aria-hidden />
						<ButtonIcon
							variant="standard"
							color="green"
							shade={5}
							size={16}
							icon={faCircleCheck}
							aria-label={`Confirm level change for ${competency.name}`}
							data-testid={`confirm-level-${index}`}
							onClick={confirmEdit}
						/>
						<ButtonIcon
							variant="standard"
							color="red"
							shade={5}
							size={16}
							icon={faCircleXmark}
							aria-label={`Cancel level change for ${competency.name}`}
							data-testid={`cancel-level-${index}`}
							onClick={cancelEdit}
						/>
					</div>
				</>
			) : (
				<>
					<div className="flex w-48 shrink-0 items-center">
						{currentLevel ? (
							<Chip label={currentLevel.name} color="neutral" size={24} />
						) : (
							<Text size="paragraph-3" weight="regular" color="neutral-7">
								—
							</Text>
						)}
					</div>
					{/* 88px trailing column with two fixed 16px slots. Pencil and X stay anchored
					    to the right; their positions don't shift when one is omitted (no-levels
					    or company-wide rows). */}
					<div className="box-border grid w-[76px] shrink-0 grid-cols-[1fr_16px_16px] items-center gap-2 pr-3">
						<div aria-hidden />
						<div className="size-4">
							{hasLevels && (
								<ButtonIcon
									variant="standard"
									color="neutral"
									shade={7}
									size={16}
									icon={faPencil}
									aria-label={`Edit level for ${competency.name}`}
									data-testid={`edit-competency-${index}`}
									onClick={enterEdit}
								/>
							)}
						</div>
						<div className="size-4">
							{!competency.is_company_wide && (
								<ButtonIcon
									variant="standard"
									color="neutral"
									shade={7}
									size={16}
									icon={faCircleXmark}
									aria-label={`Remove ${competency.name}`}
									data-testid={`remove-competency-${index}`}
									onClick={onRemove}
								/>
							)}
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export default AddCompetencyRow;
